import type { QueryClient } from '@tanstack/vue-query';
import { isAxiosError } from 'axios';

import { useTeamCookie } from '@/shared/hooks/use-team-cookie';

import { teamKeys } from '../keys';
import { teamQueryOptions } from '../queries';
import type { Team } from '../types';

/**
 * Resolves the team stored in the cookie. Returns null when that team no longer
 * exists (deleted, or the user was removed from it): the stale cookie and cache
 * are cleared so the caller can recover instead of leaving the user stuck.
 */
export async function resolveCurrentTeam(
  queryClient: QueryClient,
  uuid: string,
): Promise<Team | null> {
  try {
    return await queryClient.ensureQueryData(teamQueryOptions(uuid));
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      const { clearTeamCookie } = useTeamCookie();
      clearTeamCookie();
      queryClient.removeQueries({ queryKey: teamKeys.team(uuid) });
      return null;
    }

    throw error;
  }
}
